import { NativeEventEmitter, NativeModules, LogBox } from 'react-native';
import { mediaDevices, MediaStream } from '@cloudflare/react-native-webrtc';
import LocalMediaError from './LocalMediaError';
import {
  ConstrainDouble,
  RealtimeKitMediaStreamConstraints,
  RealtimeKitMediaTrackConstraints,
  MediaDeviceInfo,
} from './LocalMediaInterfaces';
import NativeAudioManager from './NativeAudioManager';
LogBox.ignoreLogs(['new NativeEventEmitter']);
export interface LocalAudioOptions {
  // Disables all audio processing
  disableAP?: boolean;

  // Disables Acoustic Echo Cancellation
  disableAEC?: boolean;

  // Disables Noise Suppression
  disableNS?: boolean;

  // Disables Automatic Gain Control
  disableAGC?: boolean;

  // Disables Highpass Filter
  disableHPF?: boolean;
}

export const VIDEO_CONSTRAINTS = {
  qvga: { width: { ideal: 320 }, height: { ideal: 240 } },
  vga: { width: { ideal: 640 }, height: { ideal: 480 } },
  hd: { width: { ideal: 1280 }, height: { ideal: 720 } },
  hd_cropped: { width: { ideal: 900 }, height: { ideal: 720 } },
};

export const deviceChangeListener = new NativeEventEmitter(
  NativeModules.InCallManager
);

/**
 * To initialize an instance of this class, use the
 * `.init()` static method.
 */
export default class LocalMediaUtils {
  #availableDevices: MediaDeviceInfo[];

  #nativeEventEmitter: NativeEventEmitter;

  #previouslySelectedDevice: string | null = null;

  private constructor(availableDevices: MediaDeviceInfo[]) {
    this.#availableDevices = availableDevices;
    this.#nativeEventEmitter = deviceChangeListener;
  }

  public static async init() {
    const availableVideoDevices = await LocalMediaUtils.enumerateVideoDevices();
    const availableAudioDevices = await LocalMediaUtils.enumerateAudioDevices();
    return new LocalMediaUtils(
      availableAudioDevices.concat(availableVideoDevices)
    );
  }

  public async destruct() {
    this.#nativeEventEmitter.removeAllListeners('onAudioDeviceChanged');
  }

  public static isDeviceListAvailable() {
    return true;
  }

  private static async enumerateVideoDevices(): Promise<MediaDeviceInfo[]> {
    try {
      const deviceMem = {};
      const videoDevices =
        (await mediaDevices.enumerateDevices()) as MediaDeviceInfo[];
      return videoDevices.filter((d) => {
        if (d.kind === 'videoinput' && !deviceMem[d.facing]) {
          deviceMem[d.facing] = true;
          return d;
        }
        return false;
      });
    } catch (error) {
      return [];
    }
  }

  private static async enumerateAudioDevices(): Promise<MediaDeviceInfo[]> {
    let audioDevices = {
      earpiece: {
        connected: true,
        haveMic: true,
      },
      speaker: {
        connected: true,
        haveMic: true,
      },
      bluetooth: {
        connected: false,
        haveMic: false,
      },
      wired: {
        connected: false,
        haveMic: false,
      },
    };
    const isWiredHeadphonesConnected =
      await NativeAudioManager.getIsWiredHeadsetPluggedIn();
    audioDevices.wired = isWiredHeadphonesConnected
      ? {
          connected: true,
          haveMic: true,
        }
      : {
          connected: false,
          haveMic: false,
        };
    const isBluetoothHeadsetConnected =
      await NativeAudioManager.isBluetoothHeadsetConnected();
    audioDevices.bluetooth = isBluetoothHeadsetConnected
      ? {
          connected: true,
          haveMic: true,
        }
      : {
          connected: false,
          haveMic: false,
        };
    const resp = Object.keys(audioDevices)
      .map((audioDevice) => {
        const audioDeviceProps = audioDevices[audioDevice];
        return {
          ...audioDeviceProps,
          deviceId: audioDevice,
          label: audioDevice,
          kind: 'audioinput',
        };
      })
      .filter((audioDevice) => audioDevice.connected);
    return resp as MediaDeviceInfo[];
  }

  public static getVideoConstraints(options?: {
    videoDeviceId?: string;
    videoTrackConstraints?: boolean | RealtimeKitMediaTrackConstraints;
    facingMode?: string;
    frameRate?: ConstrainDouble;
  }) {
    const constraints: RealtimeKitMediaStreamConstraints = {};
    const {
      videoDeviceId,
      videoTrackConstraints,
      facingMode = 'user',
      frameRate = { ideal: 24, max: 30 },
    } = options;

    constraints.video = videoTrackConstraints || VIDEO_CONSTRAINTS.vga;
    if (typeof constraints.video === 'boolean') {
      return constraints;
    }

    constraints.video.deviceId = videoDeviceId;
    constraints.video.facingMode = facingMode;
    constraints.video.frameRate = frameRate;
    return constraints;
  }

  private static async getUserMediaWithTimeout(
    constraints: RealtimeKitMediaStreamConstraints = {},
    timeout: number = 0
  ) {
    try {
      if (!timeout) {
        return await mediaDevices.getUserMedia(constraints);
      }

      // NOTE(roerohan): The await is required to catch errors (if any).
      return Promise.race<Promise<unknown>>([
        mediaDevices.getUserMedia(constraints),

        new Promise<MediaStream>((_resolve, reject) => {
          setTimeout(
            () =>
              reject(
                new LocalMediaError(
                  'Get user media timed out',
                  'ERR_GUM_TIMEOUT'
                )
              ),
            timeout
          );
        }),
      ]);
    } catch (err) {
      throw err;
    }
  }

  public static async getAudioTrack() {
    const mediaStream = (await LocalMediaUtils.getUserMediaWithTimeout({
      audio: true,
      timeout: 5000,
    })) as MediaStream;
    return mediaStream.getAudioTracks()[0];
  }

  public static async getVideoTrack(
    constraints: RealtimeKitMediaStreamConstraints
  ) {
    const mediaStream = (await LocalMediaUtils.getUserMediaWithTimeout({
      ...constraints,
      timeout: 5000,
    })) as MediaStream;
    return mediaStream.getVideoTracks()[0];
  }

  public static async getScreenShareTracks() {
    /**
     * getDisplayMedia is not defined on type MediaDevices.
     * Need to update this when a fix
     * is made: https://github.com/microsoft/TypeScript/issues/33232
     */
    const stream: MediaStream =
      (await mediaDevices.getDisplayMedia()) as MediaStream;
    return {
      video: stream.getVideoTracks()[0],
      audio: stream.getAudioTracks()[0],
    };
  }

  public static async getAudioAndVideoTrack(
    constraints: RealtimeKitMediaStreamConstraints
  ) {
    try {
      const mediaStream = (await LocalMediaUtils.getUserMediaWithTimeout({
        ...constraints,
        timeout: 5000,
      })) as MediaStream;
      return {
        audioTrack: mediaStream.getAudioTracks()[0],
        videoTrack: mediaStream.getVideoTracks()[0],
      };
    } catch (err) {
      throw err;
    }
  }

  public onDeviceChange(
    callback: (
      changedDevices: {
        added: MediaDeviceInfo[];
        removed: MediaDeviceInfo[];
        selectedDevice?: string;
      },
      forceDeviceChange?: boolean
    ) => void
  ) {
    const onChange = async (deviceData) => {
      const checkDeviceId = (_id: string) => {
        if (_id.toLowerCase().includes('speaker')) return 'speaker';
        else return _id;
      };

      // Extract selected device from event
      const selectedDevice = deviceData.selectedAudioDevice;
      const hasSelectedDeviceChanged =
        selectedDevice &&
        this.#previouslySelectedDevice !== null &&
        selectedDevice !== this.#previouslySelectedDevice;

      // On first event, we need to initialize previouslySelectedDevice and fire callback
      const isFirstDeviceEvent =
        selectedDevice && this.#previouslySelectedDevice === null;

      // Update previouslySelectedDevice IMMEDIATELY after detection to avoid race conditions
      // This must happen BEFORE any async operations
      if (selectedDevice) {
        this.#previouslySelectedDevice = selectedDevice;
      }

      const previouslyAvailableDevices = this.#availableDevices.filter(
        (device) =>
          device.kind === 'audioinput' || device.kind === 'audiooutput'
      );
      const previouslyAvailableDeviceIds = previouslyAvailableDevices.map(
        (device) => checkDeviceId(device.deviceId)
      );

      // Parse available device list from event to detect list changes
      let availableDeviceListFromEvent: string[] = [];
      const len = deviceData.availableAudioDeviceList.length;
      if (typeof deviceData.availableAudioDeviceList === 'string') {
        try {
          availableDeviceListFromEvent = JSON.parse(
            deviceData.availableAudioDeviceList
          );
        } catch (e) {
          // If parsing fails, fall back to empty array
          availableDeviceListFromEvent = [];
        }
      }
      if (len === 0) {
        const currentlyAvailableDevices =
          await LocalMediaUtils.enumerateAudioDevices();
        const currentlyAvailableDeviceIds = currentlyAvailableDevices.map(
          (device) => checkDeviceId(device.deviceId)
        );

        const changedDevices = {
          added: currentlyAvailableDevices.filter(
            (device) =>
              !previouslyAvailableDeviceIds.includes(
                checkDeviceId(device.deviceId)
              )
          ),
          removed: previouslyAvailableDevices.filter(
            (device) => !currentlyAvailableDeviceIds.includes(device.deviceId)
          ),
          selectedDevice: selectedDevice,
        };

        // Check if available device list changed
        const hasAvailableListChanged =
          availableDeviceListFromEvent.length > 0 &&
          (availableDeviceListFromEvent.length !==
            previouslyAvailableDeviceIds.length ||
            !availableDeviceListFromEvent.every((device) =>
              previouslyAvailableDeviceIds.includes(device)
            ));

        this.repopulateAvailableDevices().then(() => {
          const shouldFireCallback =
            changedDevices.added.length ||
            changedDevices.removed.length ||
            hasSelectedDeviceChanged ||
            isFirstDeviceEvent ||
            hasAvailableListChanged;

          if (shouldFireCallback) {
            callback(changedDevices, false);
          }
        });
      } else {
        const currentlyAvailableDevices =
          await LocalMediaUtils.enumerateAudioDevices();
        const currentlyAvailableDeviceIds = currentlyAvailableDevices.map(
          (device) => checkDeviceId(device.deviceId)
        );
        const availableAudioDevices =
          await LocalMediaUtils.enumerateAudioDevices();

        const changedDevices = {
          added: availableAudioDevices.filter(
            (device) =>
              !previouslyAvailableDeviceIds.includes(
                checkDeviceId(device.deviceId)
              )
          ),
          removed: previouslyAvailableDevices.filter(
            (device) => !currentlyAvailableDeviceIds.includes(device.deviceId)
          ),
          selectedDevice: selectedDevice,
        };

        // Check if available device list changed
        const hasAvailableListChanged =
          availableDeviceListFromEvent.length > 0 &&
          (availableDeviceListFromEvent.length !==
            previouslyAvailableDeviceIds.length ||
            !availableDeviceListFromEvent.every((device) =>
              previouslyAvailableDeviceIds.includes(device)
            ));

        this.repopulateAvailableDevices().then(() => {
          const shouldFireCallback =
            changedDevices.added.length ||
            changedDevices.removed.length ||
            hasSelectedDeviceChanged ||
            isFirstDeviceEvent ||
            hasAvailableListChanged;

          if (shouldFireCallback) {
            callback(changedDevices, false);
          }
        });
      }
    };
    if (this.#nativeEventEmitter.listenerCount('onAudioDeviceChanged') === 0) {
      this.#nativeEventEmitter.addListener('onAudioDeviceChanged', onChange);
    }
  }

  public async repopulateAvailableDevices() {
    const videoDevices = await LocalMediaUtils.enumerateVideoDevices();
    const audioDevices = await LocalMediaUtils.enumerateAudioDevices();
    this.#availableDevices = videoDevices.concat(audioDevices);
  }

  public async chooseAudioRoute(route: String) {
    return await NativeAudioManager.chooseAudioRoute(route);
  }

  public get availableDevices() {
    return this.#availableDevices;
  }

  public getAvailableDevicesByKind(kind: string) {
    return this.#availableDevices.filter((device) => device.kind === kind);
  }

  public getDeviceById(
    deviceId: string,
    kind: 'audioinput' | 'audiooutput' | 'videoinput'
  ) {
    const normalizedDeviceId = deviceId.toLowerCase();

    return this.#availableDevices.find((device) => {
      if (device.kind !== kind) return false;

      const deviceIdLower = device.deviceId.toLowerCase();

      // Exact match first
      if (device.deviceId === deviceId) return true;

      // iOS special cases
      if (
        normalizedDeviceId === 'speaker' &&
        deviceIdLower.includes('speaker')
      ) {
        return true;
      }
      if (
        normalizedDeviceId === 'bluetooth' &&
        deviceIdLower.includes('bluetooth')
      ) {
        return true;
      }
      if (
        normalizedDeviceId === 'earpiece' &&
        deviceIdLower.includes('earpiece')
      ) {
        return true;
      }

      return false;
    });
  }

  /**
   * Returns all audio input devices (microphone devices).
   */
  public async getAudioDevices() {
    await this.repopulateAvailableDevices();
    return this.getAvailableDevicesByKind('audioinput');
  }

  /**
   * Returns all video input devices.
   */
  public async getVideoDevices() {
    await this.repopulateAvailableDevices();
    return this.getAvailableDevicesByKind('videoinput');
  }

  /**
   * Returns all audio output devices (speaker devices).
   */
  public async getSpeakerDevices() {
    await this.repopulateAvailableDevices();
    const speakerDevices = this.#availableDevices;
    // In Mobile, speaker devices are the same as audio input devices
    return speakerDevices
      .filter((device) => device.kind === 'audioinput')
      .map((device) => ({
        ...device,
        deviceId: device.deviceId,
        label: device.label,
        kind: 'audiooutput',
      }));
  }
}
